// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { DotsSixVertical, PlusCircle } from '@phosphor-icons/react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import { XGButton } from 'xg-cl';
import {
  addPassengerFieldToCollectionRound,
  createPassengerCollectionRound,
  deletePassengerCollectionRoundField,
  deletePassengerCollectionRound,
  getPassengerCollectionRounds,
  updatePassengerCollectionRoundFieldAssignmentType,
  updatePassengerCollectionRoundFieldSequence,
  updatePassengerFieldStatus,
  updatePassengerFieldSequence,
  updatePassengerCollectionRound,
  updatePassengerCollectionRoundSequence
} from '../../../../services/tenant/tenant';
import { ErrorToast, SuccessToast } from '../../../../utils/alerts';
import { reorderItems } from '../../../../utils/common';
import DeleteModal from '../../../Trip/DeleteModal/DeleteModal';
import UpdateModal from '../../../Trip/UpdateModal/UpdateModal';
import UnsavedChangesModal from '../../../Trip/Itinerary/UnsavedChangesModal/UnsavedChangesModal';
import PassengerCollectionRoundModal from '../PassengerCollectionRoundModal/PassengerCollectionRoundModal';
import CollectionRoundCard from '../../../PassengerCollection/CollectionRoundCard/CollectionRoundCard';
import CollectionFieldRow from '../../../PassengerCollection/CollectionFieldRow/CollectionFieldRow';
import CollectionLibraryDrawer from '../../../PassengerCollection/CollectionLibraryDrawer/CollectionLibraryDrawer';
import removeFieldFromCollectionRounds from '../../../PassengerCollection/removeFieldFromCollectionRounds';
import * as PropTypes from 'prop-types';
import './PassengerCollectionPanel.css';

const INITIAL_FORM = {
  passengercollectionroundid: null,
  name: '',
  collectionanchortype: 'before_departure',
  sendoffsetdays: null,
  dueoffsetdays: '',
  errors: {}
};

const getComparableRoundForm = (formState = {}) => ({
  passengercollectionroundid: formState?.passengercollectionroundid ?? null,
  name: formState?.name ?? '',
  collectionanchortype: formState?.collectionanchortype ?? 'before_departure',
  sendoffsetdays: formState?.sendoffsetdays ?? '',
  dueoffsetdays: formState?.dueoffsetdays ?? ''
});

const ROUND_LIST_DROPPABLE_ID = 'passenger-collection-rounds';
const LIBRARY_DROPPABLE_ID = 'passenger-collection-library';
const AT_BOOKING_ROUND_KEY = 'at-booking';
const INTERNAL_FIELDS_KEY = 'internal-fields';
const SIDEBAR_ROUND_DROPPABLE_PREFIX = 'passenger-collection-sidebar-round-';
const ROUND_DROPPABLE_TYPE = 'PASSENGER_COLLECTION_ROUND';
const FIELD_DROPPABLE_TYPE = 'PASSENGER_COLLECTION_FIELD';
const REQUIRED_ASSIGNMENT_TYPE = 'required';
const OPTIONAL_ASSIGNMENT_TYPE = 'optional';
const PINNED_SYSTEM_FIELD_TITLES = ['First Name', 'Last Name', 'Email Address'];

const getTargetSequenceForInsert = (fields = [], destinationIndex = null) => {
  if (destinationIndex === null || destinationIndex === undefined) {
    return null;
  }

  if (!fields.length) {
    return 1;
  }

  if (destinationIndex <= 0) {
    return fields[0]?.sequence ?? 1;
  }

  if (destinationIndex >= fields.length) {
    const lastField = fields[fields.length - 1];
    return (lastField?.sequence ?? fields.length) + 1;
  }

  return fields[destinationIndex]?.sequence ?? destinationIndex + 1;
};

const getLockedSystemFieldCount = (fields = []) => {
  let lockedCount = 0;

  for (const field of fields) {
    if (!field?.issystemfield) {
      break;
    }

    lockedCount += 1;
  }

  return lockedCount;
};

const normalizeDestinationIndex = (fields = [], destinationIndex = null) => {
  if (destinationIndex === null || destinationIndex === undefined) {
    return destinationIndex;
  }

  return Math.max(destinationIndex, getLockedSystemFieldCount(fields));
};

const isLockedAtBookingSection = (section) =>
  Boolean(section?.isSyntheticAtBooking);

const PassengerCollectionPanel = ({
  passengerFields = [],
  openCreateRoundRequest = 0,
  openLibraryDrawerRequest = 0,
  headerActions = null,
  refreshPassengerFields,
  syncPassengerFieldRoundUsage,
  handleOnEditField
}) => {
  const { t } = useTranslation(['settings', 'common', 'errors']);
  const [isMounted, setIsMounted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rounds, setRounds] = useState([]);
  const [selectedRoundKey, setSelectedRoundKey] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [roundPendingDelete, setRoundPendingDelete] = useState(null);
  const [pendingFieldMove, setPendingFieldMove] = useState(null);
  const [formState, setFormState] = useState(INITIAL_FORM);
  const [isLibraryDrawerOpen, setIsLibraryDrawerOpen] = useState(false);
  const [librarySearchValue, setLibrarySearchValue] = useState('');
  const [localSectionFieldsOverride, setLocalSectionFieldsOverride] =
    useState(null);
  const [isLibraryFieldDragging, setIsLibraryFieldDragging] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const lastHandledCreateRoundRequestRef = useRef(openCreateRoundRequest);
  const lastHandledLibraryDrawerRequestRef = useRef(openLibraryDrawerRequest);
  const initialRoundFormRef = useRef(getComparableRoundForm(INITIAL_FORM));
  const pendingRoundModalActionRef = useRef(null);
  const prevPassengerFieldsRef = useRef(passengerFields);

  useEffect(() => {
    setLocalSectionFieldsOverride(null);
  }, [passengerFields]);

  const internalFields = useMemo(() => {
    if (localSectionFieldsOverride?.key === INTERNAL_FIELDS_KEY) {
      return localSectionFieldsOverride.fields;
    }

    return [...(passengerFields ?? [])]
      .filter((field) => field?.isinternal)
      .sort((fieldA, fieldB) => {
        const sequenceA = fieldA?.sequence ?? Number.MAX_SAFE_INTEGER;
        const sequenceB = fieldB?.sequence ?? Number.MAX_SAFE_INTEGER;

        if (sequenceA !== sequenceB) {
          return sequenceA - sequenceB;
        }

        return (fieldA?.title ?? '').localeCompare(fieldB?.title ?? '');
      });
  }, [localSectionFieldsOverride, passengerFields]);

  const libraryFields = useMemo(() => {
    return [...(passengerFields ?? [])].sort((fieldA, fieldB) => {
      if (fieldA?.issystemfield !== fieldB?.issystemfield) {
        return fieldA?.issystemfield ? -1 : 1;
      }

      if (fieldA?.issystemfield) {
        const orderA = PINNED_SYSTEM_FIELD_TITLES.indexOf(fieldA?.title ?? '');
        const orderB = PINNED_SYSTEM_FIELD_TITLES.indexOf(fieldB?.title ?? '');
        const pinnedA =
          orderA === -1 ? PINNED_SYSTEM_FIELD_TITLES.length : orderA;
        const pinnedB =
          orderB === -1 ? PINNED_SYSTEM_FIELD_TITLES.length : orderB;
        if (pinnedA !== pinnedB) return pinnedA - pinnedB;
      }

      return (fieldA?.title ?? '').localeCompare(fieldB?.title ?? '');
    });
  }, [passengerFields]);

  const isAtBookingRound = useCallback(
    (round) =>
      round?.key === AT_BOOKING_ROUND_KEY ||
      round?.isSyntheticAtBooking ||
      (round?.collectionanchortype === 'after_booking' &&
        round?.sendoffsetdays === 0),
    []
  );

  const getRoundDisplayName = useCallback(
    (round) => (isAtBookingRound(round) ? t('atBooking') : round?.name),
    [isAtBookingRound, t]
  );

  const getRoundSubtitle = useCallback(
    (round) => {
      if (isAtBookingRound(round)) {
        return t('atBookingSubtitle');
      }

      const anchorLabel =
        round?.collectionanchortype === 'after_booking'
          ? t('afterBooking')
          : t('beforeDeparture');

      if (round?.dueoffsetdays !== null && round?.dueoffsetdays !== undefined) {
        return t('roundDueSummary', {
          count: round.dueoffsetdays,
          anchor: anchorLabel
        });
      }

      if (
        round?.sendoffsetdays !== null &&
        round?.sendoffsetdays !== undefined
      ) {
        return t('roundSendSummary', {
          count: round.sendoffsetdays,
          anchor: anchorLabel
        });
      }

      return anchorLabel;
    },
    [isAtBookingRound, t]
  );

  const atBookingRound = useMemo(() => {
    const atBookingFields =
      localSectionFieldsOverride?.key === AT_BOOKING_ROUND_KEY
        ? localSectionFieldsOverride.fields
        : [...(passengerFields ?? [])]
            .filter(
              (field) =>
                !field?.isinternal && (field?.isrequired || field?.isoptional)
            )
            .sort((fieldA, fieldB) => {
              const sequenceA = fieldA?.sequence ?? Number.MAX_SAFE_INTEGER;
              const sequenceB = fieldB?.sequence ?? Number.MAX_SAFE_INTEGER;

              if (sequenceA !== sequenceB) {
                return sequenceA - sequenceB;
              }

              return (fieldA?.title ?? '').localeCompare(fieldB?.title ?? '');
            })
            .map((field) => ({
              passengerfieldid: field?.passengerfieldid,
              title: field?.title,
              assignmenttype: field?.isrequired ? 'required' : 'optional',
              issystemfield: field?.issystemfield,
              sequence: field?.sequence ?? null
            }));

    return {
      key: AT_BOOKING_ROUND_KEY,
      passengercollectionroundid: null,
      name: t('atBooking'),
      subtitle: t('atBookingSubtitle'),
      collectionanchortype: 'after_booking',
      sendoffsetdays: 0,
      dueoffsetdays: null,
      fieldcount: atBookingFields.length,
      fields: atBookingFields,
      isSyntheticAtBooking: true
    };
  }, [localSectionFieldsOverride, passengerFields, t]);

  const roundList = useMemo(() => {
    return [atBookingRound, ...rounds]
      .sort((roundA, roundB) => {
        const roundABooking = isAtBookingRound(roundA);
        const roundBBooking = isAtBookingRound(roundB);

        if (roundABooking !== roundBBooking) {
          return roundABooking ? -1 : 1;
        }

        return (roundA?.sequence ?? 0) - (roundB?.sequence ?? 0);
      })
      .map((round) => ({
        ...round,
        key:
          round?.key ??
          (round?.passengercollectionroundid
            ? String(round?.passengercollectionroundid)
            : AT_BOOKING_ROUND_KEY),
        name: getRoundDisplayName(round),
        subtitle: getRoundSubtitle(round)
      }));
  }, [
    atBookingRound,
    getRoundDisplayName,
    getRoundSubtitle,
    isAtBookingRound,
    rounds
  ]);

  const reorderableRoundList = useMemo(
    () => roundList.filter((round) => !isAtBookingRound(round)),
    [isAtBookingRound, roundList]
  );

  const selectedActualRound =
    roundList.find((round) => round.key === String(selectedRoundKey)) ?? null;

  const selectedRoundFieldIds = useMemo(() => {
    return new Set(
      (selectedActualRound?.fields ?? []).map(
        (field) => field?.passengerfieldid
      )
    );
  }, [selectedActualRound]);

  const roundFieldUsageMap = useMemo(() => {
    return roundList.reduce((usageMap, round) => {
      (round?.fields ?? []).forEach((field) => {
        if (!usageMap.has(field?.passengerfieldid)) {
          usageMap.set(field?.passengerfieldid, []);
        }

        usageMap.get(field?.passengerfieldid).push({
          roundid: round?.passengercollectionroundid,
          roundname: round?.name,
          assignmenttype: field?.assignmenttype,
          issystemfield: field?.issystemfield
        });
      });

      return usageMap;
    }, new Map());
  }, [roundList]);

  const filteredLibraryFields = useMemo(() => {
    const normalizedSearch = librarySearchValue.trim().toLowerCase();

    return libraryFields.filter((field) => {
      if (!normalizedSearch) {
        return true;
      }

      return field?.title?.toLowerCase().includes(normalizedSearch);
    });
  }, [libraryFields, librarySearchValue]);

  const getRoundFieldDroppableId = useCallback(
    (roundId) => `passenger-collection-round-fields-${roundId}`,
    []
  );

  const getSidebarRoundDroppableId = useCallback(
    (roundKey) => `${SIDEBAR_ROUND_DROPPABLE_PREFIX}${roundKey}`,
    []
  );

  const getSelectedRoundDroppableId = useCallback(
    (round) =>
      getRoundFieldDroppableId(
        round?.passengercollectionroundid ?? round?.key ?? AT_BOOKING_ROUND_KEY
      ),
    [getRoundFieldDroppableId]
  );

  const replaceRoundInState = useCallback(
    (nextRound) => {
      if (!nextRound?.passengercollectionroundid) {
        return;
      }

      setRounds((prevRounds) => {
        const nextRounds = prevRounds.map((round) =>
          round?.passengercollectionroundid ===
          nextRound.passengercollectionroundid
            ? nextRound
            : round
        );

        syncPassengerFieldRoundUsage?.(nextRounds);
        return nextRounds;
      });
    },
    [syncPassengerFieldRoundUsage]
  );

  const isEditableAssignmentField = useCallback(
    (field) => !field?.issystemfield,
    []
  );

  const isReorderableRoundField = useCallback(
    (field) => !field?.issystemfield,
    []
  );

  const getUsageBadgeLabel = useCallback(
    (field) => {
      if (field?.isinternal) {
        return t('internalField');
      }

      const usageEntries =
        roundFieldUsageMap.get(field?.passengerfieldid) ?? [];

      if (!usageEntries.length) {
        return null;
      }

      const firstRequiredUsage = usageEntries.find(
        (usage) => usage?.assignmenttype === REQUIRED_ASSIGNMENT_TYPE
      );

      if (firstRequiredUsage?.roundname) {
        return t('requiredOnCollectionRound', {
          round: firstRequiredUsage.roundname
        });
      }

      return t('inACollectionRound');
    },
    [roundFieldUsageMap, t]
  );

  const getUsageBadgeVariant = useCallback(
    (field) => {
      if (field?.isinternal) {
        return 'muted';
      }

      const usageEntries =
        roundFieldUsageMap.get(field?.passengerfieldid) ?? [];
      const firstRequiredUsage = usageEntries.find(
        (usage) => usage?.assignmenttype === REQUIRED_ASSIGNMENT_TYPE
      );

      return firstRequiredUsage?.roundname ? 'info' : 'muted';
    },
    [roundFieldUsageMap]
  );

  const getMoveFieldModalCopy = useCallback(
    (field, targetRound) => {
      if (targetRound?.type === 'internal') {
        return {
          title: t('moveFieldToInternalTitle'),
          description: t('moveFieldToInternalDescription', {
            field: field?.title ?? ''
          })
        };
      }

      return {
        title: t('moveFieldFromInternalTitle'),
        description: t('moveFieldFromInternalDescription', {
          field: field?.title ?? '',
          target: targetRound?.name ?? targetRound?.title ?? t('atBooking')
        })
      };
    },
    [t]
  );

  const getFieldAddedSuccessMessage = useCallback(
    (targetRound) =>
      targetRound?.type === 'internal'
        ? t('fieldAddedToInternalFieldsSuccessfully')
        : t('fieldAddedToCollectionRoundSuccessfully'),
    [t]
  );

  const getFieldAddedErrorMessage = useCallback(
    (targetRound) =>
      targetRound?.type === 'internal'
        ? t('fieldCouldNotBeAddedToInternalFields')
        : t('fieldCouldNotBeAddedToCollectionRound'),
    [t]
  );

  const internalFieldsSection = useMemo(
    () => ({
      key: INTERNAL_FIELDS_KEY,
      type: 'internal',
      title: t('internalFields'),
      fields: internalFields
    }),
    [internalFields, t]
  );

  const selectedSection = useMemo(() => {
    if (selectedRoundKey === INTERNAL_FIELDS_KEY) {
      return internalFieldsSection;
    }

    if (selectedActualRound) {
      return {
        ...selectedActualRound,
        type: 'round'
      };
    }

    return null;
  }, [internalFieldsSection, selectedActualRound, selectedRoundKey]);

  const fetchRounds = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data = [] } = await getPassengerCollectionRounds();
      const nextRounds = data ?? [];
      setRounds(nextRounds);
      syncPassengerFieldRoundUsage?.(nextRounds);
      return nextRounds;
    } catch (error) {
      setRounds([]);
      ErrorToast(t('errors:common.internalServerError'));
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [syncPassengerFieldRoundUsage, t]);

  const fetchRoundsRef = useRef(fetchRounds);

  // Re-fetch rounds only when field content actually changes
  useEffect(() => {
    if (!isMounted) return;
    if (prevPassengerFieldsRef.current === passengerFields) return;

    const serialize = (fields) =>
      fields
        ?.map(
          (f) =>
            `${f.passengerfieldid}:${f.title}:${f.isrequired}:${f.isoptional}:${f.isinternal}`
        )
        .sort()
        .join('|') ?? '';

    const prevSerialized = serialize(prevPassengerFieldsRef.current);
    const nextSerialized = serialize(passengerFields);

    prevPassengerFieldsRef.current = passengerFields;

    if (prevSerialized !== nextSerialized) {
      fetchRoundsRef.current();
    }
  }, [passengerFields, isMounted]);

  useEffect(() => {
    setIsMounted(true);
    fetchRounds();
  }, [fetchRounds]);

  useEffect(() => {
    const availableKeys = new Set(
      rounds.map((round) => String(round?.passengercollectionroundid))
    );
    availableKeys.add(AT_BOOKING_ROUND_KEY);
    availableKeys.add(INTERNAL_FIELDS_KEY);

    if (selectedRoundKey && availableKeys.has(String(selectedRoundKey))) {
      return;
    }

    if (atBookingRound) {
      setSelectedRoundKey(atBookingRound.key);
      return;
    }

    if (roundList.length > 0) {
      setSelectedRoundKey(roundList[0].key);
      return;
    }

    setSelectedRoundKey(null);
  }, [atBookingRound, roundList, rounds, selectedRoundKey]);

  const onOpenCreateModal = () => {
    setIsLibraryDrawerOpen(false);
    setFormState(INITIAL_FORM);
    initialRoundFormRef.current = getComparableRoundForm(INITIAL_FORM);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (openCreateRoundRequest <= lastHandledCreateRoundRequestRef.current) {
      return;
    }

    lastHandledCreateRoundRequestRef.current = openCreateRoundRequest;
    onOpenCreateModal();
  }, [openCreateRoundRequest]);

  useEffect(() => {
    if (
      openLibraryDrawerRequest <= lastHandledLibraryDrawerRequestRef.current
    ) {
      return;
    }

    lastHandledLibraryDrawerRequestRef.current = openLibraryDrawerRequest;
    setIsLibraryDrawerOpen(true);
  }, [openLibraryDrawerRequest]);

  const onOpenEditModal = (round) => {
    const nextFormState = {
      passengercollectionroundid: round?.passengercollectionroundid,
      name: round?.name ?? '',
      collectionanchortype: round?.collectionanchortype ?? 'before_departure',
      sendoffsetdays:
        round?.sendoffsetdays !== null && round?.sendoffsetdays !== undefined
          ? String(round.sendoffsetdays)
          : null,
      dueoffsetdays:
        round?.dueoffsetdays !== null && round?.dueoffsetdays !== undefined
          ? String(round.dueoffsetdays)
          : '',
      errors: {}
    };

    setIsLibraryDrawerOpen(false);
    setFormState(nextFormState);
    initialRoundFormRef.current = getComparableRoundForm(nextFormState);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setFormState(INITIAL_FORM);
    setShowUnsavedModal(false);
    pendingRoundModalActionRef.current = null;
    initialRoundFormRef.current = getComparableRoundForm(INITIAL_FORM);
  }, []);

  const isRoundFormDirty = useCallback(() => {
    if (!isModalOpen) {
      return false;
    }

    return (
      JSON.stringify(getComparableRoundForm(formState)) !==
      JSON.stringify(initialRoundFormRef.current)
    );
  }, [formState, isModalOpen]);

  const onCloseModal = useCallback(() => {
    if (isSubmitting) {
      return;
    }

    if (isRoundFormDirty()) {
      pendingRoundModalActionRef.current = closeModal;
      setShowUnsavedModal(true);
      return;
    }

    closeModal();
  }, [closeModal, isRoundFormDirty, isSubmitting]);

  const validateForm = () => {
    const errors = {};
    const sendOffsetDays = Number(formState?.sendoffsetdays);
    const dueOffsetDays = Number(formState?.dueoffsetdays);
    const daysRequiredMessage = t('collectionRoundDaysRequiredDynamic', {
      anchor:
        formState?.collectionanchortype === 'after_booking'
          ? t('afterBooking')
          : t('beforeDeparture')
    });
    const normalizedName = formState?.name?.trim().toLowerCase();
    const hasDuplicateName =
      normalizedName &&
      rounds.some(
        (round) =>
          (round?.name ?? '').trim().toLowerCase() === normalizedName &&
          round?.passengercollectionroundid !==
            formState?.passengercollectionroundid
      );

    if (!formState?.name?.trim()) {
      errors.name = t('collectionRoundNameRequired');
    } else if (hasDuplicateName) {
      errors.name = t('collectionRoundNameDuplicate');
    }

    if (
      !['after_booking', 'before_departure'].includes(
        formState?.collectionanchortype
      )
    ) {
      errors.collectionanchortype = t('errors:common.invalidData');
    }

    if (
      formState?.sendoffsetdays === '' ||
      formState?.sendoffsetdays === null ||
      formState?.sendoffsetdays === undefined
    ) {
      errors.sendoffsetdays = daysRequiredMessage;
    } else if (
      !/^\d+$/.test(String(formState?.sendoffsetdays)) ||
      Number(formState?.sendoffsetdays) < 0
    ) {
      errors.sendoffsetdays = daysRequiredMessage;
    }

    if (
      formState?.dueoffsetdays === '' ||
      formState?.dueoffsetdays === null ||
      formState?.dueoffsetdays === undefined
    ) {
      errors.dueoffsetdays = daysRequiredMessage;
    } else if (
      !/^\d+$/.test(String(formState?.dueoffsetdays)) ||
      Number(formState?.dueoffsetdays) < 0
    ) {
      errors.dueoffsetdays = daysRequiredMessage;
    }

    if (
      !errors.sendoffsetdays &&
      !errors.dueoffsetdays &&
      ((formState?.collectionanchortype === 'before_departure' &&
        sendOffsetDays < dueOffsetDays) ||
        (formState?.collectionanchortype === 'after_booking' &&
          sendOffsetDays > dueOffsetDays))
    ) {
      errors.sendoffsetdays = t('collectionRoundTimingOrderInvalid');
      errors.dueoffsetdays = t('collectionRoundTimingOrderInvalid');
    }

    setFormState((prev) => ({
      ...prev,
      errors
    }));

    return Object.keys(errors).length === 0;
  };

  const handleSaveRound = async () => {
    if (!validateForm()) {
      return false;
    }

    const payload = {
      name: formState.name.trim(),
      collectionanchortype: formState.collectionanchortype,
      sendoffsetdays:
        formState.sendoffsetdays === '' || formState.sendoffsetdays === null
          ? null
          : Number(formState.sendoffsetdays),
      dueoffsetdays:
        formState.dueoffsetdays === '' ? null : Number(formState.dueoffsetdays)
    };

    try {
      setIsSubmitting(true);

      if (formState.passengercollectionroundid) {
        await updatePassengerCollectionRound(
          formState.passengercollectionroundid,
          payload
        );
        SuccessToast(t('collectionRoundUpdatedSuccessfully'));
      } else {
        const { data } = await createPassengerCollectionRound(payload);
        const createdRoundId = data?.passengercollectionroundid;
        SuccessToast(t('collectionRoundCreatedSuccessfully'));
        await fetchRounds();
        if (createdRoundId) {
          setSelectedRoundKey(String(createdRoundId));
        }
        closeModal();
        return true;
      }

      await fetchRounds();
      closeModal();
      return true;
    } catch (error) {
      ErrorToast(t('collectionRoundCouldNotBeSaved'));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnsavedModalAction = async (action) => {
    const queuedAction = pendingRoundModalActionRef.current;

    switch (action) {
      case 'continue':
        setShowUnsavedModal(false);
        pendingRoundModalActionRef.current = null;
        break;
      case 'discard':
        setShowUnsavedModal(false);
        if (typeof queuedAction === 'function') {
          queuedAction();
        }
        pendingRoundModalActionRef.current = null;
        break;
      case 'save':
        await handleSaveRound();
        setShowUnsavedModal(false);
        pendingRoundModalActionRef.current = null;
        break;
      default:
        break;
    }
  };

  const handleDeleteRound = async () => {
    if (!roundPendingDelete?.passengercollectionroundid) {
      return;
    }

    try {
      setIsSubmitting(true);
      await deletePassengerCollectionRound(
        roundPendingDelete.passengercollectionroundid
      );
      SuccessToast(t('collectionRoundDeletedSuccessfully'));
      setIsDeleteModalOpen(false);
      setRoundPendingDelete(null);
      await fetchRounds();
      await refreshPassengerFields?.();
    } catch (error) {
      ErrorToast(t('collectionRoundCouldNotBeDeleted'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRoundReorder = useCallback(
    async (sourceIndex, destinationIndex) => {
      const sourceRound = reorderableRoundList[sourceIndex];
      const destinationRound = reorderableRoundList[destinationIndex];

      if (!sourceRound || !destinationRound) {
        return;
      }

      const originalRounds = [...rounds];
      const reorderedReorderableRounds = reorderItems(
        reorderableRoundList,
        sourceIndex,
        destinationIndex
      );

      const reorderedById = new Map(
        reorderedReorderableRounds.map((round, index) => [
          round.passengercollectionroundid,
          index + 1
        ])
      );

      setRounds((prevRounds) =>
        prevRounds.map((round) =>
          reorderedById.has(round.passengercollectionroundid)
            ? {
                ...round,
                sequence: reorderedById.get(round.passengercollectionroundid)
              }
            : round
        )
      );

      try {
        setIsSubmitting(true);
        await updatePassengerCollectionRoundSequence(
          sourceRound.passengercollectionroundid,
          {
            sequence: destinationRound.sequence
          }
        );
      } catch (error) {
        setRounds(originalRounds);
        ErrorToast(t('collectionRoundSequenceCouldNotBeUpdated'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [reorderableRoundList, rounds, t]
  );

  const handleFieldReorder = useCallback(
    async (sourceIndex, destinationIndex) => {
      if (
        !selectedSection ||
        !['round', 'internal'].includes(selectedSection?.type) ||
        sourceIndex === destinationIndex
      ) {
        return;
      }

      const lockedSystemFieldCount = isLockedAtBookingSection(selectedSection)
        ? getLockedSystemFieldCount(selectedSection.fields)
        : 0;
      const sourceFieldIndex = sourceIndex + lockedSystemFieldCount;
      const rawDestinationIndex = destinationIndex + lockedSystemFieldCount;
      const sourceField = selectedSection.fields?.[sourceFieldIndex];
      const normalizedDestinationIndex = normalizeDestinationIndex(
        selectedSection.fields,
        rawDestinationIndex
      );
      const destinationField =
        selectedSection.fields?.[normalizedDestinationIndex];

      if (
        !sourceField ||
        !destinationField ||
        sourceField?.issystemfield ||
        normalizedDestinationIndex === sourceFieldIndex
      ) {
        return;
      }

      const reorderedFields = reorderItems(
        selectedSection.fields,
        sourceFieldIndex,
        normalizedDestinationIndex
      ).map((field, index) => ({
        ...field,
        sequence: index + 1
      }));

      if (
        selectedSection?.isSyntheticAtBooking ||
        selectedSection?.type === 'internal'
      ) {
        const sectionKey = selectedSection?.isSyntheticAtBooking
          ? AT_BOOKING_ROUND_KEY
          : INTERNAL_FIELDS_KEY;
        const originalFields = selectedSection.fields;

        setLocalSectionFieldsOverride({
          key: sectionKey,
          fields: reorderedFields
        });

        try {
          setIsSubmitting(true);
          await updatePassengerFieldSequence(sourceField.passengerfieldid, {
            sequence:
              destinationField.sequence ?? normalizedDestinationIndex + 1
          });
        } catch (error) {
          setLocalSectionFieldsOverride({
            key: sectionKey,
            fields: originalFields
          });
          ErrorToast(t('errors:passengerFieldSequenceCouldNotBeUpdated'));
        } finally {
          setIsSubmitting(false);
        }

        return;
      }

      const originalRounds = [...rounds];

      setRounds((prevRounds) =>
        prevRounds.map((round) =>
          round?.passengercollectionroundid ===
          selectedSection?.passengercollectionroundid
            ? {
                ...round,
                fields: reorderedFields,
                fieldcount: reorderedFields.length
              }
            : round
        )
      );

      try {
        setIsSubmitting(true);
        const { data, error } =
          await updatePassengerCollectionRoundFieldSequence(
            selectedSection.passengercollectionroundid,
            sourceField.passengerfieldid,
            {
              sequence:
                destinationField.sequence ?? normalizedDestinationIndex + 1
            }
          );

        if (!data || error) {
          setRounds(originalRounds);
          ErrorToast(t('errors:passengerFieldSequenceCouldNotBeUpdated'));
          return;
        }

        replaceRoundInState(data);
      } catch (error) {
        setRounds(originalRounds);
        ErrorToast(t('errors:passengerFieldSequenceCouldNotBeUpdated'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [replaceRoundInState, rounds, selectedSection, t]
  );

  const handleAssignFieldToRound = useCallback(
    async (
      passengerfieldid,
      targetRound,
      { destinationIndex = null, skipMoveConfirmation = false } = {}
    ) => {
      if (!targetRound || !passengerfieldid) {
        return;
      }

      const sourceField = passengerFields.find(
        (field) => field?.passengerfieldid === passengerfieldid
      );

      if (!sourceField) {
        return;
      }

      const targetRoundFieldIds = new Set(
        (targetRound?.fields ?? []).map((field) => field?.passengerfieldid)
      );
      const normalizedDestinationIndex = isLockedAtBookingSection(targetRound)
        ? normalizeDestinationIndex(
            targetRound?.fields,
            (destinationIndex ?? 0) +
              getLockedSystemFieldCount(targetRound?.fields)
          )
        : normalizeDestinationIndex(targetRound?.fields, destinationIndex);

      if (targetRoundFieldIds.has(passengerfieldid)) {
        return;
      }

      const usageEntries = roundFieldUsageMap.get(passengerfieldid) ?? [];
      const needsMoveFromInternal =
        !skipMoveConfirmation &&
        sourceField?.isinternal &&
        targetRound?.type !== 'internal';
      const needsMoveToInternal =
        !skipMoveConfirmation &&
        targetRound?.type === 'internal' &&
        usageEntries.length > 0;

      if (needsMoveFromInternal || needsMoveToInternal) {
        const moveCopy = getMoveFieldModalCopy(sourceField, targetRound);
        setPendingFieldMove({
          passengerfieldid,
          targetRound,
          destinationIndex: normalizedDestinationIndex,
          title: moveCopy.title,
          description: moveCopy.description
        });
        return;
      }

      try {
        setIsSubmitting(true);

        if (targetRound?.isSyntheticAtBooking) {
          const originalFields = targetRound?.fields ?? [];
          const targetSequence = getTargetSequenceForInsert(
            originalFields,
            normalizedDestinationIndex
          );
          const optimisticFields = [
            ...originalFields.slice(0, normalizedDestinationIndex),
            {
              passengerfieldid,
              title: sourceField?.title,
              assignmenttype: OPTIONAL_ASSIGNMENT_TYPE,
              issystemfield: sourceField?.issystemfield,
              sequence: targetSequence ?? normalizedDestinationIndex + 1
            },
            ...originalFields.slice(normalizedDestinationIndex)
          ].map((field, index) => ({
            ...field,
            sequence: index + 1
          }));

          setLocalSectionFieldsOverride({
            key: AT_BOOKING_ROUND_KEY,
            fields: optimisticFields
          });

          const { data, error } = await updatePassengerFieldStatus(
            passengerfieldid,
            {
              isrequired: false,
              isoptional: true,
              ishidden: sourceField?.ishidden ?? false,
              isinternal: false
            }
          );

          if (!data || error) {
            setLocalSectionFieldsOverride({
              key: AT_BOOKING_ROUND_KEY,
              fields: originalFields
            });
            ErrorToast(getFieldAddedErrorMessage(targetRound));
            return;
          }

          if (targetSequence !== null) {
            const { error: sequenceError } = await updatePassengerFieldSequence(
              passengerfieldid,
              {
                sequence: targetSequence
              }
            );

            if (sequenceError) {
              setLocalSectionFieldsOverride({
                key: AT_BOOKING_ROUND_KEY,
                fields: originalFields
              });
              ErrorToast(getFieldAddedErrorMessage(targetRound));
              return;
            }
          }

          await refreshPassengerFields?.();
          SuccessToast(getFieldAddedSuccessMessage(targetRound));
          return;
        }

        if (targetRound?.type === 'internal') {
          const originalRounds = rounds;
          const originalFields = targetRound?.fields ?? [];
          const targetSequence = getTargetSequenceForInsert(
            originalFields,
            normalizedDestinationIndex
          );
          const optimisticFields = [
            ...originalFields.slice(0, normalizedDestinationIndex),
            {
              passengerfieldid,
              title: sourceField?.title,
              assignmenttype: null,
              issystemfield: sourceField?.issystemfield,
              sequence: targetSequence ?? normalizedDestinationIndex + 1
            },
            ...originalFields.slice(normalizedDestinationIndex)
          ].map((field, index) => ({
            ...field,
            sequence: index + 1
          }));

          setLocalSectionFieldsOverride({
            key: INTERNAL_FIELDS_KEY,
            fields: optimisticFields
          });
          setRounds((prevRounds) => {
            const nextRounds = removeFieldFromCollectionRounds(
              prevRounds,
              usageEntries,
              passengerfieldid,
              {
                roundIdKey: 'passengercollectionroundid',
                fieldIdKey: 'passengerfieldid'
              }
            );
            syncPassengerFieldRoundUsage?.(nextRounds);
            return nextRounds;
          });

          for (const usageEntry of usageEntries) {
            if (!usageEntry?.roundid) {
              continue;
            }

            const { error } = await deletePassengerCollectionRoundField(
              usageEntry.roundid,
              passengerfieldid
            );

            if (error) {
              setRounds(originalRounds);
              syncPassengerFieldRoundUsage?.(originalRounds);
              setLocalSectionFieldsOverride({
                key: INTERNAL_FIELDS_KEY,
                fields: originalFields
              });
              ErrorToast(getFieldAddedErrorMessage(targetRound));
              return;
            }
          }

          const { data, error } = await updatePassengerFieldStatus(
            passengerfieldid,
            {
              isrequired: false,
              isoptional: false,
              ishidden: sourceField?.ishidden ?? false,
              isinternal: true
            }
          );

          if (!data || error) {
            setRounds(originalRounds);
            syncPassengerFieldRoundUsage?.(originalRounds);
            setLocalSectionFieldsOverride({
              key: INTERNAL_FIELDS_KEY,
              fields: originalFields
            });
            ErrorToast(getFieldAddedErrorMessage(targetRound));
            return;
          }

          if (targetSequence !== null) {
            const { error: sequenceError } = await updatePassengerFieldSequence(
              passengerfieldid,
              {
                sequence: targetSequence
              }
            );

            if (sequenceError) {
              setRounds(originalRounds);
              syncPassengerFieldRoundUsage?.(originalRounds);
              setLocalSectionFieldsOverride({
                key: INTERNAL_FIELDS_KEY,
                fields: originalFields
              });
              ErrorToast(getFieldAddedErrorMessage(targetRound));
              return;
            }
          }

          await refreshPassengerFields?.();
          SuccessToast(getFieldAddedSuccessMessage(targetRound));
          return;
        }

        if (sourceField?.isinternal) {
          const { data, error } = await updatePassengerFieldStatus(
            passengerfieldid,
            {
              isrequired: false,
              isoptional: false,
              ishidden: sourceField?.ishidden ?? false,
              isinternal: false
            }
          );

          if (!data || error) {
            ErrorToast(getFieldAddedErrorMessage(targetRound));
            return;
          }
        }

        const { data, error } = await addPassengerFieldToCollectionRound(
          targetRound.passengercollectionroundid,
          {
            passengerfieldid
          }
        );

        if (!data || error) {
          ErrorToast(getFieldAddedErrorMessage(targetRound));
          return;
        }

        const targetSequence = getTargetSequenceForInsert(
          targetRound?.fields,
          normalizedDestinationIndex
        );

        if (targetSequence !== null) {
          const { data: reorderedRound, error: sequenceError } =
            await updatePassengerCollectionRoundFieldSequence(
              targetRound.passengercollectionroundid,
              passengerfieldid,
              {
                sequence: targetSequence
              }
            );

          if (!reorderedRound || sequenceError) {
            ErrorToast(getFieldAddedErrorMessage(targetRound));
            return;
          }

          replaceRoundInState(reorderedRound);
        } else {
          replaceRoundInState(data);
        }

        await refreshPassengerFields?.();
        SuccessToast(getFieldAddedSuccessMessage(targetRound));
      } catch (error) {
        ErrorToast(getFieldAddedErrorMessage(targetRound));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      getFieldAddedErrorMessage,
      getFieldAddedSuccessMessage,
      getMoveFieldModalCopy,
      passengerFields,
      refreshPassengerFields,
      replaceRoundInState,
      roundFieldUsageMap,
      rounds,
      syncPassengerFieldRoundUsage
    ]
  );

  const handleConfirmFieldMove = useCallback(async () => {
    if (!pendingFieldMove?.passengerfieldid || !pendingFieldMove?.targetRound) {
      return;
    }

    const nextMove = pendingFieldMove;
    setPendingFieldMove(null);
    await handleAssignFieldToRound(
      nextMove.passengerfieldid,
      nextMove.targetRound,
      {
        destinationIndex: nextMove.destinationIndex,
        skipMoveConfirmation: true
      }
    );
  }, [handleAssignFieldToRound, pendingFieldMove]);

  const handleFieldAssignmentTypeChange = useCallback(
    async (field, assignmenttype) => {
      if (
        !field?.passengerfieldid ||
        !selectedSection ||
        !isEditableAssignmentField(field) ||
        field?.assignmenttype === assignmenttype
      ) {
        return;
      }

      if (
        ![REQUIRED_ASSIGNMENT_TYPE, OPTIONAL_ASSIGNMENT_TYPE].includes(
          assignmenttype
        )
      ) {
        return;
      }

      try {
        setIsSubmitting(true);

        if (selectedSection?.isSyntheticAtBooking) {
          const targetField = passengerFields.find(
            (passengerField) =>
              passengerField?.passengerfieldid === field?.passengerfieldid
          );

          const originalFields = selectedSection.fields;
          const updatedFields = selectedSection.fields.map((f) =>
            f?.passengerfieldid === field?.passengerfieldid
              ? { ...f, assignmenttype }
              : f
          );
          setLocalSectionFieldsOverride({
            key: AT_BOOKING_ROUND_KEY,
            fields: updatedFields
          });

          const { data, error } = await updatePassengerFieldStatus(
            field.passengerfieldid,
            {
              isrequired: assignmenttype === REQUIRED_ASSIGNMENT_TYPE,
              isoptional: assignmenttype === OPTIONAL_ASSIGNMENT_TYPE,
              ishidden: targetField?.ishidden ?? false,
              isinternal: false
            }
          );

          if (!data || error) {
            setLocalSectionFieldsOverride({
              key: AT_BOOKING_ROUND_KEY,
              fields: originalFields
            });
            ErrorToast(t('fieldCouldNotBeAddedToCollectionRound'));
          }
          return;
        }

        const { data, error } =
          await updatePassengerCollectionRoundFieldAssignmentType(
            selectedSection.passengercollectionroundid,
            field.passengerfieldid,
            {
              assignmenttype
            }
          );

        if (!data || error) {
          ErrorToast(t('fieldCouldNotBeAddedToCollectionRound'));
          return;
        }

        replaceRoundInState(data);
      } catch (error) {
        ErrorToast(t('fieldCouldNotBeAddedToCollectionRound'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isEditableAssignmentField,
      passengerFields,
      replaceRoundInState,
      selectedSection,
      t
    ]
  );

  const handleRemoveFieldFromRound = useCallback(
    async (field) => {
      if (
        !field?.passengerfieldid ||
        !selectedSection ||
        !isEditableAssignmentField(field)
      ) {
        return;
      }

      try {
        setIsSubmitting(true);

        if (selectedSection?.isSyntheticAtBooking) {
          const targetField = passengerFields.find(
            (passengerField) =>
              passengerField?.passengerfieldid === field?.passengerfieldid
          );

          const { data, error } = await updatePassengerFieldStatus(
            field.passengerfieldid,
            {
              isrequired: false,
              isoptional: false,
              ishidden: targetField?.ishidden ?? false,
              isinternal: false
            }
          );

          if (!data || error) {
            ErrorToast(t('fieldCouldNotBeRemovedFromCollectionRound'));
            return;
          }

          await refreshPassengerFields?.();
          SuccessToast(t('fieldRemovedFromCollectionRoundSuccessfully'));
          return;
        }

        if (selectedSection?.type === 'internal') {
          const targetField = passengerFields.find(
            (passengerField) =>
              passengerField?.passengerfieldid === field?.passengerfieldid
          );

          const { data, error } = await updatePassengerFieldStatus(
            field.passengerfieldid,
            {
              isrequired: targetField?.isrequired ?? false,
              isoptional: targetField?.isoptional ?? false,
              ishidden: targetField?.ishidden ?? false,
              isinternal: false
            }
          );

          if (!data || error) {
            ErrorToast(t('fieldCouldNotBeRemovedFromCollectionRound'));
            return;
          }

          await refreshPassengerFields?.();
          SuccessToast(t('fieldRemovedFromCollectionRoundSuccessfully'));
          return;
        }

        const { data, error } = await deletePassengerCollectionRoundField(
          selectedSection.passengercollectionroundid,
          field.passengerfieldid
        );

        if (!data || error) {
          ErrorToast(t('fieldCouldNotBeRemovedFromCollectionRound'));
          return;
        }

        replaceRoundInState(data);
        await refreshPassengerFields?.();
        SuccessToast(t('fieldRemovedFromCollectionRoundSuccessfully'));
      } catch (error) {
        ErrorToast(t('fieldCouldNotBeRemovedFromCollectionRound'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isEditableAssignmentField,
      passengerFields,
      refreshPassengerFields,
      replaceRoundInState,
      selectedSection,
      t
    ]
  );

  const handleDragEnd = async (result) => {
    setIsLibraryFieldDragging(false);

    const { source, destination } = result;

    if (!destination) {
      return;
    }

    if (
      source.droppableId === ROUND_LIST_DROPPABLE_ID &&
      destination.droppableId === ROUND_LIST_DROPPABLE_ID &&
      source.index !== destination.index
    ) {
      await handleRoundReorder(source.index, destination.index);
      return;
    }

    if (
      ['round', 'internal'].includes(selectedSection?.type) &&
      source.droppableId === getSelectedRoundDroppableId(selectedSection) &&
      destination.droppableId === getSelectedRoundDroppableId(selectedSection)
    ) {
      await handleFieldReorder(source.index, destination.index);
      return;
    }

    if (source.droppableId === LIBRARY_DROPPABLE_ID) {
      const fieldId = String(result?.draggableId ?? '').replace(
        'library-field-',
        ''
      );

      if (!fieldId || !destination.droppableId) {
        return;
      }

      if (
        selectedSection &&
        destination.droppableId === getSelectedRoundDroppableId(selectedSection)
      ) {
        await handleAssignFieldToRound(Number(fieldId), selectedSection, {
          destinationIndex: destination.index
        });
        return;
      }

      if (destination.droppableId.startsWith(SIDEBAR_ROUND_DROPPABLE_PREFIX)) {
        const targetRoundKey = destination.droppableId.replace(
          SIDEBAR_ROUND_DROPPABLE_PREFIX,
          ''
        );
        const targetRound =
          targetRoundKey === INTERNAL_FIELDS_KEY
            ? internalFieldsSection
            : roundList.find((round) => String(round?.key) === targetRoundKey);

        if (!targetRound) {
          return;
        }

        setSelectedRoundKey(targetRoundKey);
        await handleAssignFieldToRound(Number(fieldId), targetRound, {
          destinationIndex: targetRound?.fields?.length ?? 0
        });
      }
    }
  };

  const handleDragStart = useCallback((start) => {
    setIsLibraryFieldDragging(
      start?.source?.droppableId === LIBRARY_DROPPABLE_ID
    );
  }, []);

  const renderFieldRows = () => {
    const lockedSystemFields = isLockedAtBookingSection(selectedSection)
      ? selectedSection.fields.filter((field) => field?.issystemfield)
      : [];
    const droppableFields = isLockedAtBookingSection(selectedSection)
      ? selectedSection.fields.filter((field) => !field?.issystemfield)
      : selectedSection.fields;

    if (['round', 'internal'].includes(selectedSection?.type) && isMounted) {
      return (
        <Droppable
          droppableId={getSelectedRoundDroppableId(selectedSection)}
          type={FIELD_DROPPABLE_TYPE}
          isDropDisabled={isSubmitting}
        >
          {(provided, snapshot) => (
            <div
              className={`passenger-collection-panel__detail-dropzone${
                snapshot.isDraggingOver || isLibraryFieldDragging
                  ? ' passenger-collection-panel__detail-dropzone--active'
                  : ''
              }`}
            >
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='passenger-collection-panel__field-list'
              >
                {lockedSystemFields.map((field) => (
                  <div
                    key={`${selectedSection?.key}-${field?.passengerfieldid}`}
                    className='passenger-collection-panel__field-row'
                  >
                    <Box className='passenger-collection-panel__field-copy'>
                      <Box className='passenger-collection-panel__field-leading-icon' />
                      <span className='passenger-collection-panel__field-title passenger-collection-panel__field-title--system'>
                        {field?.title}
                      </span>
                    </Box>
                    <Box className='passenger-collection-panel__field-actions passenger-collection-panel__field-actions--readonly'>
                      <span className='passenger-collection-panel__field-tag passenger-collection-panel__field-tag--info'>
                        {t('common:required')}
                      </span>
                      <Box className='passenger-collection-panel__field-remove-spacer' />
                    </Box>
                  </div>
                ))}

                <div className='passenger-collection-panel__field-drop-list'>
                  {!droppableFields?.length &&
                    !isLockedAtBookingSection(selectedSection) && (
                      <Box className='passenger-collection-panel__detail-empty'>
                        {selectedSection?.type === 'internal'
                          ? t('noFieldsAddedToInternalSection')
                          : t('noFieldsAddedToCollectionRound')}
                      </Box>
                    )}
                  {droppableFields.map((field, index) => (
                    <CollectionFieldRow
                      key={`${selectedSection?.key}-${field?.passengerfieldid}`}
                      field={field}
                      fieldUniqueId={`${selectedSection?.key}-${field?.passengerfieldid}`}
                      draggableId={`round-field-${selectedSection?.key}-${field?.passengerfieldid}`}
                      index={index}
                      sectionType={selectedSection?.type}
                      isSubmitting={isSubmitting}
                      onAssignmentChange={(assignmentType) =>
                        handleFieldAssignmentTypeChange(field, assignmentType)
                      }
                      onRemove={() => handleRemoveFieldFromRound(field)}
                      OnEditField={(event, field) =>
                        !isSubmitting &&
                        !field?.issystemfield &&
                        field?.passengerfieldid &&
                        handleOnEditField(field?.passengerfieldid)
                      }
                    />
                  ))}
                  {provided.placeholder}
                </div>
              </div>
            </div>
          )}
        </Droppable>
      );
    }

    return (
      <div className='passenger-collection-panel__field-list'>
        {selectedSection.fields.map((field) => (
          <div
            key={`${selectedSection?.name ?? selectedSection?.title}-${field?.passengerfieldid}`}
            className='passenger-collection-panel__field-row'
          >
            <Box className='passenger-collection-panel__field-copy'>
              {isReorderableRoundField(field) ? (
                <Box className='passenger-collection-panel__field-leading-icon'>
                  <DotsSixVertical size={16} />
                </Box>
              ) : (
                <Box className='passenger-collection-panel__field-leading-icon' />
              )}
              <span
                className={`passenger-collection-panel__field-title${field?.issystemfield ? ' passenger-collection-panel__field-title--system' : ''}`}
              >
                {field?.title}
              </span>
            </Box>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Box className='passenger-collection-panel'>
          <Box className='passenger-collection-panel__sidebar'>
            <Box className='passenger-collection-panel__sidebar-content'>
              <Box className='passenger-collection-panel__round-list'>
                {atBookingRound && (
                  <div className='passenger-collection-panel__round-item'>
                    <Droppable
                      droppableId={getSidebarRoundDroppableId(
                        atBookingRound.key
                      )}
                      type={FIELD_DROPPABLE_TYPE}
                      isDropDisabled={!isLibraryFieldDragging || isSubmitting}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`passenger-collection-panel__round-drop-target${
                            snapshot.isDraggingOver
                              ? ' passenger-collection-panel__round-drop-target--dragging-over'
                              : ''
                          }`}
                        >
                          <CollectionRoundCard
                            round={atBookingRound}
                            isActive={selectedRoundKey === atBookingRound.key}
                            onClick={() =>
                              setSelectedRoundKey(atBookingRound.key)
                            }
                            isStatic
                            showEditAction={false}
                            showDeleteAction={false}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                )}

                {isMounted && reorderableRoundList.length > 0 && (
                  <Droppable
                    droppableId={ROUND_LIST_DROPPABLE_ID}
                    type={ROUND_DROPPABLE_TYPE}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className='passenger-collection-panel__round-list-stack'
                      >
                        {reorderableRoundList.map((round, index) => (
                          <Draggable
                            key={round.key}
                            draggableId={round.key}
                            index={index}
                            isDragDisabled={isSubmitting}
                          >
                            {(draggableProvided) => (
                              <div
                                className='passenger-collection-panel__round-item'
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                              >
                                <Droppable
                                  droppableId={getSidebarRoundDroppableId(
                                    round.key
                                  )}
                                  type={FIELD_DROPPABLE_TYPE}
                                  isDropDisabled={
                                    !isLibraryFieldDragging || isSubmitting
                                  }
                                >
                                  {(sidebarProvided, sidebarSnapshot) => (
                                    <div
                                      ref={sidebarProvided.innerRef}
                                      {...sidebarProvided.droppableProps}
                                      className={`passenger-collection-panel__round-drop-target${
                                        sidebarSnapshot.isDraggingOver
                                          ? ' passenger-collection-panel__round-drop-target--dragging-over'
                                          : ''
                                      }`}
                                    >
                                      <CollectionRoundCard
                                        round={round}
                                        isActive={
                                          selectedRoundKey === round.key
                                        }
                                        onClick={() =>
                                          setSelectedRoundKey(round.key)
                                        }
                                        dragHandleProps={
                                          draggableProvided.dragHandleProps
                                        }
                                        onEdit={() => onOpenEditModal(round)}
                                        onDelete={() => {
                                          setRoundPendingDelete(round);
                                          setIsDeleteModalOpen(true);
                                        }}
                                      />
                                      {sidebarProvided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                )}
              </Box>

              <Box className='passenger-collection-panel__sidebar-action'>
                <XGButton
                  id='button-add-passenger-collection-round'
                  text={t('addRound')}
                  type='secondary'
                  size='large'
                  variant='outlined'
                  onClick={onOpenCreateModal}
                  startIcon={<PlusCircle />}
                />
              </Box>

              <Box className='passenger-collection-panel__internal-section passenger-collection-panel__global-section'>
                <div className='passenger-collection-panel__round-item'>
                  {isMounted && (
                    <Droppable
                      droppableId={getSidebarRoundDroppableId(
                        INTERNAL_FIELDS_KEY
                      )}
                      type={FIELD_DROPPABLE_TYPE}
                      isDropDisabled={!isLibraryFieldDragging || isSubmitting}
                    >
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`passenger-collection-panel__round-drop-target${
                            snapshot.isDraggingOver
                              ? ' passenger-collection-panel__round-drop-target--dragging-over'
                              : ''
                          }`}
                        >
                          <CollectionRoundCard
                            round={{
                              key: INTERNAL_FIELDS_KEY,
                              name: t('internalFields'),
                              fieldcount: internalFields.length
                            }}
                            isActive={selectedRoundKey === INTERNAL_FIELDS_KEY}
                            onClick={() =>
                              setSelectedRoundKey(INTERNAL_FIELDS_KEY)
                            }
                            isStatic
                            showEditAction={false}
                            showDeleteAction={false}
                          />
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  )}
                </div>
              </Box>
            </Box>
          </Box>

          <Box className='passenger-collection-panel__details'>
            {headerActions ? (
              <Box className='passenger-collection-panel__header'>
                <Box className='passenger-collection-panel__header-actions'>
                  {headerActions}
                </Box>
              </Box>
            ) : null}
            {isLoading ? (
              <Box className='passenger-collection-panel__detail-dropzone'>
                <Box className='passenger-collection-panel__detail-empty'>
                  {t('loadingCollectionRounds')}
                </Box>
              </Box>
            ) : !selectedSection ? (
              <Box className='passenger-collection-panel__detail-dropzone'>
                <Box className='passenger-collection-panel__empty-state'>
                  {t('noCollectionRoundsConfigured')}
                </Box>
              </Box>
            ) : (
              <Box className='passenger-collection-panel__detail-card'>
                {renderFieldRows()}
              </Box>
            )}
          </Box>
        </Box>

        <CollectionLibraryDrawer
          isOpen={isLibraryDrawerOpen}
          isMounted={isMounted}
          droppableId={LIBRARY_DROPPABLE_ID}
          fieldDroppableType={FIELD_DROPPABLE_TYPE}
          fields={filteredLibraryFields}
          searchInputId='passenger-collection-library-search'
          searchValue={librarySearchValue}
          onSearchChange={setLibrarySearchValue}
          onClose={() => setIsLibraryDrawerOpen(false)}
          isFieldDisabled={(field) =>
            isSubmitting ||
            !selectedSection ||
            field?.issystemfield ||
            selectedRoundFieldIds.has(field?.passengerfieldid)
          }
          getFieldKey={(field) => field?.passengerfieldid}
          getDraggableId={(field) => `library-field-${field?.passengerfieldid}`}
          getUsageBadgeLabel={getUsageBadgeLabel}
          getUsageBadgeVariant={getUsageBadgeVariant}
        />
      </DragDropContext>

      <PassengerCollectionRoundModal
        isOpen={isModalOpen}
        isSubmitting={isSubmitting}
        formState={formState}
        setFormState={setFormState}
        onClose={onCloseModal}
        onSubmit={handleSaveRound}
      />

      <UpdateModal
        id='move-passenger-field-modal'
        isOpenModal={Boolean(pendingFieldMove)}
        title={pendingFieldMove?.title}
        description={pendingFieldMove?.description}
        onConfirm={handleConfirmFieldMove}
        onCancel={() => setPendingFieldMove(null)}
        isSubmitting={isSubmitting}
      />

      <DeleteModal
        id='delete-passenger-collection-round-modal'
        isOpenModal={isDeleteModalOpen}
        title={t('deleteCollectionRound')}
        onConfirm={handleDeleteRound}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setRoundPendingDelete(null);
        }}
        description={`${t('deleteCollectionRoundConfirmation')} ${t(
          'common:thisActionCannotBeUndone'
        )}`}
        isSubmitting={isSubmitting}
      />

      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        isSubmitting={isSubmitting}
        onAction={handleUnsavedModalAction}
      />
    </>
  );
};

PassengerCollectionPanel.propTypes = {
  passengerFields: PropTypes.array,
  openCreateRoundRequest: PropTypes.number,
  openLibraryDrawerRequest: PropTypes.number,
  headerActions: PropTypes.node,
  refreshPassengerFields: PropTypes.func,
  syncPassengerFieldRoundUsage: PropTypes.func,
  handleOnEditField: PropTypes.func
};

export default PassengerCollectionPanel;
